import {
  OmniElement,
  OmniStyleElement,
  OmniIconElement,
  html,
  css,
  unsafeCSS,
  ref,
  classMap,
  createRef,
  live,
} from 'omni-ui';

import { loadScriptFromCDN, loadStylesheetFromCDN } from '../helpers/utils.js';

OmniStyleElement.register();
OmniIconElement.register();

/**
 * ------------------------------------------------------------------------
 * @todo: This is frankensteined from admin framework template and should
 * be modified to allow for various color formats instead of just rgb
 * ------------------------------------------------------------------------
 */

/**
 * Variable to change size of color picker element
 */
const pickerWidth = '14.286rem';

/**
 * Class for Color Picker Element
 * @property {string} color - RGB value of color
 * @property {boolean} isLoading - A temporary solution to avoid FOUC while waiting on the CDN
 */
export default class ColorPicker extends OmniElement {
  static get properties() {
    return {
      color: { type: String },
      isLoading: { type: Boolean },
    };
  }

  static get styles() {
    return [
      super.styles,
      css`
        .colorful {
          width: 2.143rem;
          height: 2.5rem;
          background-color: var(--pcr-color);
          border-radius: 6px 0 0 6px;
          border: 1px solid transparent;
          border-right: none;
          cursor: pointer;
        }
        .color-picker-container {
          display: flex;
          flex-direction: row-reverse;
          align-items: center;
          width: ${unsafeCSS(pickerWidth)};
        }
        .omni .input {
          flex-grow: 1;
          border-radius: 0 6px 6px 0;
        }
        .omni input:hover ~ .colorful {
          border-color: var(--color-gray-b5);
        }

        .omni input.input:focus,
        .omni input.input:active,
        .omni input.input:focus-visible,
        .omni input:focus ~ .colorful,
        .omni input:active ~ .colorful,
        .omni input:focus-visible ~ .colorful {
          /* selected color set manually so border can match */
          border-color: var(--selected-color);
        }
        .omni input,
        .omni input:hover,
        .omni input:active,
        .omni input:focus-visible {
          border-left: none !important;
        }

        /* @todo: Is this necessary? */
        div.pcr-app[data-theme='classic'] {
          width: 14.5rem;
        }
      `,
    ];
  }

  pickerRef = createRef();

  omniStyleRef = createRef();

  constructor() {
    super();
    this.color = null;
    this.isLoading = true;
  }

  /**
   * Updates color picker color value after {@link #color} is updated
   */
  updated(props) {
    /** @todo: Does keeping this a prop AND state conflict? */
    if (props.get('color')) {
      /** @todo: Check for color format? */
      this.#handleColorChange(this.color);
    }
  }

  /**
   * Create and append color picker elements to document body on initial page load
   * Uses 3rd party Pickr library https://github.com/Simonwep/pickr
   * Directs color picker elements to call {@link #_dispatchChange} on change/hide
   * NOTE: Color picker and input are separate entities
   */
  async firstUpdated() {
    // selects elements with
    await loadScriptFromCDN('pickr@1.8.2/dist/pickr.min.js', 'Pickr');
    await loadStylesheetFromCDN('pickr@1.8.2/dist/themes/classic.min.css');

    this.isLoading = false;

    const { value: pickerEl } = this.pickerRef;
    const computedOmniStyle = getComputedStyle(this.omniStyleRef.value);
    this.#setActiveColorCSSVariable(this.color);

    const swatches = Array.from(
      // make sure we don't display duplicate colors
      new Set(
        [
          '--rgb-primary',
          '--rgb-accent-one',
          '--rgb-accent-two',
          '--rgb-accent-three',
          '--rgb-accent-four',
        ].map(name => `rgb(${computedOmniStyle.getPropertyValue(name)})`),
      ),
    );

    this.picker = new Pickr({
      // Selector or element which will be replaced with the actual color-picker.
      // Can be a HTMLElement.
      el: pickerEl,

      // Where the pickr-app should be added as child.
      container: 'body',
      theme: 'classic',
      useAsButton: true,

      // Defines change/save behavior:
      // False applies color to button and preview (save) in sync with each change
      comparison: false,

      // Default color
      default: this.color,

      // Default color representation of the input/output textbox.
      // Valid options are `HEX`, `RGBA`, `HSVA`, `HSLA` and `CMYK`.
      defaultRepresentation: 'RGBA',

      // Defines the position of the color-picker.
      position: 'bottom-start',

      components: {
        hue: true, // Display hue slider
      },

      swatches,
    });

    this.picker
      .on('show', this.#fixPickrUI)
      .on('change', color => {
        const [r, g, b] = color.toRGBA().map(ele => ele.toFixed());

        this.#handleColorChange(`rgb(${r}, ${g}, ${b})`);
      })
      .on('hide', this.#dispatchChange);
  }

  /**
   * element is appended to body so DOM node has to be manually accessed to style the width of the color picker
   * @todo: This is so hacky it hurts but omni's specificity ruins it otherwise. We may be able to overcome this once we're able to fetch from the CDN and use adoptedStylesheets
   */
  #fixPickrUI = (_, { _root: { app, swatches } }) => {
    /* eslint-disable no-param-reassign */
    const swatchButtons = [...swatches.querySelectorAll('button')];

    app.style.width = pickerWidth;
    swatches.style.display = 'flex';
    swatches.style.justifyContent = 'stretch';
    swatches.style.alignItems = 'stretch';

    swatchButtons.forEach(button => {
      button.style.padding = 0;
      button.style.flexBasis = `calc(${100 / swatchButtons.length}% - 5px)`;
    });
    /* eslint-enable no-param-reassign */
  };

  #setActiveColorCSSVariable = color => {
    this.style.setProperty('--selected-color', color);
  };

  #dispatchChange = () => {
    this.dispatchNewEvent('change', { detail: { color: this.color } });
  };

  #handleColorChange = (color, updatePicker = false) => {
    this.color = color;

    if (updatePicker) this.picker.setColor(color);

    this.#setActiveColorCSSVariable(color);
    this.#dispatchChange();
  };

  #handleInputChange = ({ target }) => {
    this.#handleColorChange(target.value, true);
  };

  /**
   * @returns {html} Rendered HTML for the component
   * Uses lit live directive to set input.value if it differs from the live DOM value rather than the last-rendered value
   */
  render() {
    return html`
      <omni-style ${ref(this.omniStyleRef)}>
        <div
          class=${classMap({
            'color-picker-container': true,
            skeleton: this.isLoading,
          })}
        >
          <input
            type="text"
            class="input"
            spellcheck="false"
            @input="${this.#handleInputChange}"
            .value="${live(this.color)}"
          />
          <div ${ref(this.pickerRef)} class="colorful"></div>
        </div>
      </omni-style>
    `;
  }
}

OmniElement.register('campaign-color-picker', ColorPicker);
